// @flow strict

import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

// Constants
import { SidebarLayout, SidebarTab } from 'components/_ReactUI_V1';
import { CIRCUIT_TYPES, NAVIGATION_ROUTES, STYLE_VALUES } from 'utils/constants';

// Components
import GeoDashboard from 'microfrontends/GeoDashboard';

// Helpers
import { isSolvExtractUser, canAccessCyanex } from 'utils/authentication';

// Services
import { selectUser } from 'services/Authentication/selectors';

// Types
import type { IntlType, ReactNode } from 'types';
import type { ImmutableUser } from 'services/Authentication/types';

type Props = {
    intl: IntlType,
    user: ImmutableUser,
    history: Object,
};

class GeoDashboardPage extends React.PureComponent<Props> {
    sidebarRef = React.createRef();

    onCircuitTypeSelection = (circuitType: string) => () => {
        if (circuitType === CIRCUIT_TYPES.SOLVEXTRACT_CIRCUIT) {
            this.props.history.push(NAVIGATION_ROUTES.SOLVEXTRACT_DASHBOARD);
        } else {
            this.props.history.push(NAVIGATION_ROUTES.MINCHEM_DASHBOARD);
        }
    };

    goToCyanex = () => {
        this.props.history.push(NAVIGATION_ROUTES.CYANEX);
    };

    goToGeoDashboard = () => {
        this.props.history.push(NAVIGATION_ROUTES.GEO_DASHBOARD);
    };

    renderMainContent = () => {
        return <GeoDashboard />;
    };

    renderSidebar = (): Array<ReactNode> => {
        const showSolvExtract = isSolvExtractUser(this.props.user);
        const showCyanex = canAccessCyanex(this.props.user);

        const translateCircuitType = (type: string) =>
            this.props.intl.formatMessage({
                id: `containers.DashboardContainer.circuitTypes.${type}`,
            });

        return (
            <>
                <SidebarTab
                    active={false}
                    handleOnHeaderClick={this.onCircuitTypeSelection(CIRCUIT_TYPES.MINCHEM_CIRCUIT)}
                    title={translateCircuitType(CIRCUIT_TYPES.MINCHEM_CIRCUIT)}
                />
                {showCyanex && (
                    <SidebarTab
                        handleOnHeaderClick={this.goToCyanex}
                        title={this.props.intl.formatMessage({
                            id: 'containers.DashboardContainer.CyanexSidebarLink',
                        })}
                    />
                )}
                {showSolvExtract && (
                    <SidebarTab
                        active={false}
                        handleOnHeaderClick={this.onCircuitTypeSelection(
                            CIRCUIT_TYPES.SOLVEXTRACT_CIRCUIT
                        )}
                        title={translateCircuitType(CIRCUIT_TYPES.SOLVEXTRACT_CIRCUIT)}
                    />
                )}
                {showSolvExtract && (
                    <SidebarTab
                        active
                        handleOnHeaderClick={this.goToGeoDashboard}
                        title={this.props.intl.formatMessage({
                            id: 'containers.DashboardContainer.GeoDashboardSidebarLink',
                        })}
                    />
                )}
            </>
        );
    };

    render() {
        return (
            <React.Fragment>
                <Helmet>
                    <title>
                        {this.props.intl.formatMessage({
                            id: 'views.GeoDashboardPage.helmetTitle',
                        })}
                    </title>
                </Helmet>
                <SidebarLayout
                    ref={this.sidebarRef}
                    styles={{
                        sidebar: {
                            height: `calc(100vh - ${STYLE_VALUES.HEADER.HEIGHT})`,
                        },
                        main: {
                            height: `calc(100vh - ${STYLE_VALUES.HEADER.HEIGHT})`,
                            overflowY: 'hidden',
                        },
                    }}
                    renderMain={this.renderMainContent}
                    renderSidebar={this.renderSidebar}
                    sidebarWidth={STYLE_VALUES.SIDEBAR.WIDTH}
                    responsiveMaxDeviceWidth={STYLE_VALUES.RESPONSIVE_AT_LARGE}
                    responsiveTitle={this.props.intl.formatMessage({
                        id: 'containers.DashboardContainer.mobileSidebarTitle',
                    })}
                    responsive
                    collapsible
                    flush
                    mainFlush
                />
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector({
    user: selectUser(),
});

export default withRouter(connect(mapStateToProps)(injectIntl(GeoDashboardPage)));
